import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import type {
  Payment,
  Plan,
  Subscription,
  SubscriptionUsage,
} from '../models/billing.model';
import type { WithSlice } from '@reduxjs/toolkit';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

interface AsyncSlot<T> {
  data: T;
  status: LoadStatus;
  error: string | null;
}

export interface BillingState {
  subscription: AsyncSlot<Subscription | null>;
  usage: AsyncSlot<SubscriptionUsage | null>;
  payments: {
    data: Payment[];
    total: number;
    status: LoadStatus;
    error: string | null;
  };
  publicPlans: AsyncSlot<Plan[]>;
  /** Shared flag for changePlan / cancelSubscription mutations. */
  mutating: boolean;
}

const initialState: BillingState = {
  subscription: { data: null, status: 'idle', error: null },
  usage: { data: null, status: 'idle', error: null },
  payments: { data: [], total: 0, status: 'idle', error: null },
  publicPlans: { data: [], status: 'idle', error: null },
  mutating: false,
};

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setSubscriptionLoading: (state) => {
      state.subscription.status = 'loading';
      state.subscription.error = null;
    },
    setSubscriptionData: (
      state,
      action: PayloadAction<Subscription | null>,
    ) => {
      state.subscription.data = action.payload;
      state.subscription.status = 'success';
      state.subscription.error = null;
    },
    setSubscriptionError: (state, action: PayloadAction<string>) => {
      state.subscription.status = 'error';
      state.subscription.error = action.payload;
    },

    setUsageLoading: (state) => {
      state.usage.status = 'loading';
      state.usage.error = null;
    },
    setUsageData: (state, action: PayloadAction<SubscriptionUsage | null>) => {
      state.usage.data = action.payload;
      state.usage.status = 'success';
      state.usage.error = null;
    },
    setUsageError: (state, action: PayloadAction<string>) => {
      state.usage.status = 'error';
      state.usage.error = action.payload;
    },

    setPaymentsLoading: (state) => {
      state.payments.status = 'loading';
      state.payments.error = null;
    },
    setPaymentsData: (
      state,
      action: PayloadAction<{ data: Payment[]; total: number }>,
    ) => {
      state.payments.data = action.payload.data;
      state.payments.total = action.payload.total;
      state.payments.status = 'success';
      state.payments.error = null;
    },
    setPaymentsError: (state, action: PayloadAction<string>) => {
      state.payments.status = 'error';
      state.payments.error = action.payload;
    },

    setPublicPlansLoading: (state) => {
      state.publicPlans.status = 'loading';
      state.publicPlans.error = null;
    },
    setPublicPlansData: (state, action: PayloadAction<Plan[]>) => {
      state.publicPlans.data = action.payload;
      state.publicPlans.status = 'success';
      state.publicPlans.error = null;
    },
    setPublicPlansError: (state, action: PayloadAction<string>) => {
      state.publicPlans.status = 'error';
      state.publicPlans.error = action.payload;
    },

    setMutating: (state, action: PayloadAction<boolean>) => {
      state.mutating = action.payload;
    },

    resetBilling: () => initialState,
  },
});

export const {
  setSubscriptionLoading,
  setSubscriptionData,
  setSubscriptionError,
  setUsageLoading,
  setUsageData,
  setUsageError,
  setPaymentsLoading,
  setPaymentsData,
  setPaymentsError,
  setPublicPlansLoading,
  setPublicPlansData,
  setPublicPlansError,
  setMutating,
  resetBilling,
} = billingSlice.actions;

const selectSlice = (state: RootState): BillingState =>
  state.billing ?? initialState;

export const selectBillingSubscription = (state: RootState) =>
  selectSlice(state).subscription;
export const selectBillingUsage = (state: RootState) =>
  selectSlice(state).usage;
export const selectBillingPayments = (state: RootState) =>
  selectSlice(state).payments;
export const selectBillingPublicPlans = (state: RootState) =>
  selectSlice(state).publicPlans;
export const selectBillingMutating = (state: RootState) =>
  selectSlice(state).mutating;

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof billingSlice> {}
}
